import { IconButton } from '@mui/material';
import { ExamsColumns } from 'components/common/enhanced/common-headers';
import { EnhancedDataGrid } from 'components/common/enhanced/data-grid';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { examService, examTagService, macroExamService } from 'services/api';
import ProductToCostDialog from './ProductToCostDialog';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { HistoryLineList } from 'components/common/HistoryList/list/HisotryLineList';
import { examHistoryLineService } from 'services/api/history/ExamHistoryService';
import { ExamHistoryLineColumns } from 'components/common/enhanced/common-headers/exam-history-lines';
import { isBottomFromTenantAdmin } from 'components/helpers/IsBottomFromTenantAdmin';
import toast from 'features/toast';
import { IOption } from 'interfaces';

interface ProductExamsProps {
  productId: string | number;
}

function ProductExams(props: ProductExamsProps) {
  const { t } = useTranslation();
  const { productId } = props;

  const datagridRefresh = useRef(null);
  const [mode, setMode] = useState('');
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [macroExamOptions, setMacroExamOptions] = useState<string[]>([]);

  const isTenantAdminBottom = isBottomFromTenantAdmin();

  useEffect(() => {
    examTagService
      .getAllBaseInformation()
      .then((tags: IOption[]) => {
        if (tags && Array.isArray(tags)) {
          setTagOptions(tags.map((tag) => tag.code));
        } else {
          setTagOptions([]);
        }
      })
      .catch(() => {
        setTagOptions([]);
      });
    macroExamService
      .getAllBaseInformation()
      .then((macroExams: IOption[]) => {
        if (macroExams && Array.isArray(macroExams)) {
          setMacroExamOptions(macroExams.map((me) => me.code));
        } else {
          setMacroExamOptions([]);
        }
      })
      .catch(() => {
        setMacroExamOptions([]);
      });
  }, []);

  const reloadExams = () => {
    if (datagridRefresh) datagridRefresh.current();
  };

  const getExams = (limit = 0, offset = 0, params: Record<string, unknown> = {}) => {
    return examService.getAllPaginated(limit, offset, { ...params, product: productId });
  };

  const columns = ExamsColumns(+productId, datagridRefresh, tagOptions);

  // handle the history of the product throught exams
  const historyRefresher = useRef(null);

  const handleDateTimeUpdate = (id: number, datetime: string) => {
    examHistoryLineService.update(id, { datetime: datetime }).then(() => {
      toast.success(t('actions.successfully-updated'));
      if (historyRefresher.current) {
        historyRefresher.current();
      }
    });
  };

  const handleDeleteLine = (id: number) => {
    examHistoryLineService.delete(id).then(() => {
      toast.success(t('actions.successfully-deleted'));
      if (historyRefresher.current) {
        historyRefresher.current();
      }
    });
  };

  const manualRefresh = () => {
    if (historyRefresher.current) {
      historyRefresher.current();
    }
  };

  const revertedColumnsExams = useMemo(
    () =>
      ExamHistoryLineColumns(
        handleDateTimeUpdate,
        handleDeleteLine,
        true,
        tagOptions,
        macroExamOptions
      ),
    [handleDateTimeUpdate, handleDeleteLine, tagOptions, macroExamOptions]
  );

  return (
    <>
      <b className="text-primary-600">{t('menu.exams')}</b>
      {isTenantAdminBottom && (
        <IconButton color="primary" onClick={() => setMode('exams')}>
          <AddCircleOutlineIcon />
        </IconButton>
      )}
      <b className="text-primary-600">{t('menu.macro-exams')}</b>
      {isTenantAdminBottom && (
        <IconButton color="primary" onClick={() => setMode('macroexams')}>
          <AddCircleOutlineIcon />
        </IconButton>
      )}
      <br />
      <EnhancedDataGrid
        getMethod={getExams}
        service={examService}
        columns={columns}
        refresh={datagridRefresh}
        disableEdit={!isTenantAdminBottom}
        disableExport={true}
      />
      <br />
      <ProductToCostDialog
        productId={+productId}
        mode={mode}
        setMode={setMode}
        callback={reloadExams}
      />
      <br />
      <HistoryLineList
        service={examHistoryLineService}
        columns={revertedColumnsExams}
        outerParams={{ product_id: productId }}
        refresh={historyRefresher}
      />
    </>
  );
}

export default ProductExams;
